import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ValuationHistory = ({ configurations = [] }) => {
  const [expandedConfig, setExpandedConfig] = useState(null);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })?.format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleLoadConfiguration = (config) => {
    console.log('Loading configuration:', config);
    // In a real app, this would populate the form with saved data
  };

  const handleDeleteConfiguration = (id) => {
    console.log('Deleting configuration:', id);
    // In a real app, this would remove the configuration from saved list
  };

  const handleCompareConfigurations = (configs) => {
    console.log('Comparing configurations:', configs);
    // In a real app, this would open a comparison view
  };

  if (configurations?.length === 0) {
    return (
      <div className="bg-surface border border-border rounded-lg shadow-card p-4">
        <h3 className="font-medium text-text-primary mb-4 flex items-center space-x-2">
          <Icon name="History" size={16} className="text-primary" />
          <span>Valuation History</span>
        </h3>
        
        <div className="text-center py-8">
          <Icon name="FileText" size={48} className="text-text-secondary mx-auto mb-4" />
          <div className="text-text-secondary mb-2">No saved valuations yet</div>
          <div className="text-sm text-text-secondary">
            Complete a valuation and save it to build your history
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface border border-border rounded-lg shadow-card p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-text-primary flex items-center space-x-2">
          <Icon name="History" size={16} className="text-primary" />
          <span>Valuation History</span>
        </h3>
        
        {configurations?.length > 1 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleCompareConfigurations(configurations?.slice(0, 3))}
            iconName="GitCompare"
            iconPosition="left"
          >
            Compare
          </Button>
        )}
      </div>
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {configurations?.map((config) => (
          <div key={config?.id} className="border border-border rounded-lg">
            <div className="p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-text-primary truncate">
                    {config?.name}
                  </div>
                  <div className="text-xs text-text-secondary">
                    {formatDate(config?.createdAt)}
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setExpandedConfig(expandedConfig === config?.id ? null : config?.id)}
                    className="p-1 hover:bg-muted rounded"
                  >
                    <Icon 
                      name={expandedConfig === config?.id ? "ChevronUp" : "ChevronDown"} 
                      size={16} 
                      className="text-text-secondary" 
                    />
                  </button>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-primary">
                  {formatCurrency(config?.valuation?.fairMarketValue || 0)}
                </div>
                <div className="flex items-center space-x-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleLoadConfiguration(config)}
                    iconName="Upload"
                    iconPosition="left"
                  >
                    Load
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteConfiguration(config?.id)}
                    iconName="Trash2"
                    className="text-error hover:text-error"
                  >
                  </Button>
                </div>
              </div>
            </div>
            
            {expandedConfig === config?.id && (
              <div className="border-t border-border p-3 bg-muted">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-text-secondary">Make/Model:</span>
                    <div className="font-medium text-text-primary">
                      {config?.data?.make} {config?.data?.model}
                    </div>
                  </div>
                  
                  <div>
                    <span className="text-text-secondary">Year:</span>
                    <div className="font-medium text-text-primary">
                      {config?.data?.year || 'N/A'}
                    </div>
                  </div>
                  
                  <div>
                    <span className="text-text-secondary">Capacity:</span>
                    <div className="font-medium text-text-primary">
                      {config?.data?.capacity ? `${config?.data?.capacity} tons` : 'N/A'}
                    </div>
                  </div>
                  
                  <div>
                    <span className="text-text-secondary">Hours:</span>
                    <div className="font-medium text-text-primary">
                      {config?.data?.hours ? config?.data?.hours?.toLocaleString() : 'N/A'}
                    </div>
                  </div>
                  
                  <div>
                    <span className="text-text-secondary">Condition:</span>
                    <div className="font-medium text-text-primary">
                      {config?.data?.condition ? `${config?.data?.condition}/5` : 'N/A'}
                    </div>
                  </div>
                  
                  <div>
                    <span className="text-text-secondary">Confidence:</span>
                    <div className="font-medium text-text-primary">
                      {config?.valuation?.confidenceScore || 0}%
                    </div>
                  </div>
                </div>
                
                {config?.valuation?.valueRange && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <div className="text-xs text-text-secondary mb-1">Value Range</div>
                    <div className="text-sm font-medium text-text-primary">
                      {formatCurrency(config?.valuation?.valueRange?.min)} - {formatCurrency(config?.valuation?.valueRange?.max)}
                    </div>
                  </div>
                )}
                
                {config?.valuation?.recommendations && config?.valuation?.recommendations?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <div className="text-xs text-text-secondary mb-2">Key Recommendations</div>
                    <div className="space-y-1">
                      {config?.valuation?.recommendations?.slice(0, 2)?.map((rec, index) => (
                        <div key={index} className="flex items-start space-x-1">
                          <Icon name="ArrowRight" size={12} className="text-primary mt-1 flex-shrink-0" />
                          <span className="text-xs text-text-primary">{rec}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      {configurations?.length > 5 && (
        <div className="mt-3 pt-3 border-t border-border text-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => console.log('View all configurations')}
            iconName="MoreHorizontal"
            iconPosition="left"
          >
            View All ({configurations?.length})
          </Button>
        </div>
      )}
      <div className="mt-3 pt-3 border-t border-border">
        <div className="text-xs text-text-secondary text-center">
          <Icon name="Shield" size={12} className="inline mr-1" />
          Your saved configurations are stored locally and private
        </div>
      </div>
    </div>
  );
};

export default ValuationHistory;